// 夜の色ガラス: a night becomes a stained-glass window. Each pool is a pane in
// the shape of its basin, set in a shoji frame. Lit water is glass in the gel
// of the crab who closed it; every piece sits in thick black came, drawn from
// smoothed masks so the pieces are curves, not cells (the same rule as the
// pool screen, see game/gels.js).
import { BASINS } from '../sim/basins.js';
import { gridFrame, CELL } from '../sim/grid.js';
import { SHELLS } from '../sim/shells.js';
import { phaseIndex } from '../sim/moon.js';
import { blurInPlace, nightGelHex } from '../game/gels.js';

const CRAB_HEX = ['#9fd16a', '#ff8a5c', '#ffc23a', '#b9a2ff'];
const rgbOf = h => [1, 3, 5].map(k => parseInt(h.slice(k, k + 2), 16));
const GOLD = [236, 170, 56], NIGHT = [24, 30, 72], DARK = [10, 8, 16];
// The unlit water of each game, as in its pool screen.
const WATER = { pen: [24, 32, 72], stars: [8, 10, 24], lantern: [86, 50, 16], floats: [16, 52, 70], picture: [112, 102, 80] };
const LAYERS = 10;   // 0..7 gels, 8 undertow, 9 open water
// Canvas text uses the same family as the UI (the CSS --serif token).
const uiFont = () => (typeof document !== 'undefined' && getComputedStyle(document.body).getPropertyValue('--serif').trim()) || '"Shippori Mincho", serif';

function decode(rle, n) {
  const out = new Uint8Array(n);
  let k = 0;
  for (let i = 0; i < rle.length; i += 2) { out.fill(rle[i], k, k + rle[i + 1]); k += rle[i + 1]; }
  return out;
}
function hash(x, y) { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
// Smooth value noise, so the glass ripples rather than showing squares.
function vnoise(x, y) {
  const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const a = hash(ix, iy), b = hash(ix + 1, iy), c = hash(ix, iy + 1), d = hash(ix + 1, iy + 1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
const smooth = (a, b, x) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

// Records from before gels were stored only know which crabs drew a pen.
const gelFromMask = v => { const m = v - 1; return m === 16 ? 6 : m === 0 ? 0 : 1 + Math.log2(m & -m); };

// Draw one pane (pool) into ctx at (ox, oy), `ppc` pixels per cell.
function drawPane(ctx, rec, ox, oy, ppc, { pattern = '', phase = 4, game = 'pen' } = {}) {
  const shellHex = (rec.colors || []).map((sh, i) => SHELLS[sh]?.glass || CRAB_HEX[i]);
  while (shellHex.length < 4) shellHex.push(CRAB_HEX[shellHex.length]);
  const gels = nightGelHex(phase, shellHex).map(rgbOf);
  const water = WATER[rec.mode || game] || WATER.pen;
  const basin = BASINS.find(b => b.id === rec.basin);
  const poly = basin.outline.map(([x, y]) => [x * 4, y * 4]);
  const fr = gridFrame(poly);
  const w = rec.w, h = rec.h, n = w * h;
  const cells = decode(rec.rle, n);
  const gelOf = rec.gel ? decode(rec.gel, n) : null;
  const picture = rec.st ? new Uint8Array(n) : null;
  if (picture) for (const i of rec.st) picture[i] = 1;
  // One smoothed field per layer (and one for the picture outline).
  const layers = Array.from({ length: LAYERS }, () => new Float32Array(n));
  for (let i = 0; i < n; i++) {
    const v = cells[i];
    if (v >= 1 && v <= 17) layers[picture ? (picture[i] ? 5 : 0) : Math.min(7, gelOf ? gelOf[i] : gelFromMask(v))][i] = 1;
    else if (v === 18) layers[8][i] = 1;
    else if (v === 19) layers[9][i] = 1;
  }
  const tmp = new Float32Array(n);
  for (const f of layers) blurInPlace(w, h, f, tmp);
  const pic = picture ? blurInPlace(w, h, Float32Array.from(picture), tmp) : null;
  const colour = id => (id === 8 ? DARK : id === 9 ? water : picture ? (id === 5 ? GOLD : NIGHT) : gels[id]);

  const W = w * ppc, H = h * ppc;
  const img = shadeGlass(ctx, { layers, w, h, ppc, colour, pattern, pic });
  // Draw through a scratch canvas so the pane composes over the window.
  const pane = document.createElement('canvas'); pane.width = W; pane.height = H;
  pane.getContext('2d').putImageData(img, 0, 0);
  ctx.drawImage(pane, ox, oy);
  // Thick came round the pane, along the basin outline.
  ctx.save();
  ctx.translate(ox, oy);
  ctx.beginPath();
  poly.forEach(([x, y], k) => {
    const px = ((x - fr.x0) / CELL) * ppc, py = ((y - fr.y0) / CELL) * ppc;
    k ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
  });
  ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(3, ppc * 1.1);
  ctx.strokeStyle = '#040306';
  ctx.stroke();
  // Star nights: the scooped stars and the constellation they made.
  if (rec.stars?.length) {
    const pts = rec.stars.map(([sx, sy, lit]) => [(sx + 0.5) * ppc, (sy + 0.5) * ppc, lit]);
    ctx.strokeStyle = 'rgba(255,214,130,0.85)'; ctx.lineWidth = Math.max(1, ppc * 0.22);
    ctx.beginPath();
    pts.filter(p => p[2]).forEach(([x, y], k) => (k ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
    for (const [x, y, lit] of pts) star(ctx, x, y, lit ? ppc * 1.3 : ppc * 0.5, lit ? '#fff2c0' : 'rgba(160,170,200,0.4)');
  }
  ctx.restore();
  return { W, H };
}

// Shade smoothed layers into glass: the strongest layer at each pixel picks
// the colour, came runs where two layers are level, `pic` adds the motif line.
function shadeGlass(ctx, { layers, w, h, ppc, colour, pattern = '', pic = null }) {
  const W = w * ppc, H = h * ppc;
  const img = ctx.createImageData(W, H);
  const lw = 0.13;   // came width, in units of the smoothed fields (about 0.6 of a cell)
  const N = layers.length;
  for (let py = 0; py < H; py++) {
    const gy = py / ppc - 0.5, cy = Math.max(0, Math.min(h - 2, Math.floor(gy))), fy = Math.max(0, Math.min(1, gy - cy));
    for (let px = 0; px < W; px++) {
      const gx = px / ppc - 0.5, cx = Math.max(0, Math.min(w - 2, Math.floor(gx))), fx = Math.max(0, Math.min(1, gx - cx));
      const i00 = cx + cy * w, w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
      let sum = 0, id1 = -1, m1 = 0, m2 = 0;
      for (let L = 0; L < N; L++) {
        const f = layers[L];
        const v = f[i00] * w00 + f[i00 + 1] * w10 + f[i00 + w] * w01 + f[i00 + w + 1] * w11;
        sum += v;
        if (v > m1) { m2 = m1; m1 = v; id1 = L; } else if (v > m2) m2 = v;
      }
      const k = (px + py * W) * 4;
      const alpha = smooth(0.35, 0.6, sum);
      if (alpha <= 0) { img.data[k + 3] = 0; continue; }
      const d = m1 - m2;
      let lead = 1 - smooth(lw * 0.7, lw, d);
      // Picture nights: the motif's outline is part of the leading.
      if (pic) {
        const pv = pic[i00] * w00 + pic[i00 + 1] * w10 + pic[i00 + w] * w01 + pic[i00 + w + 1] * w11;
        lead = Math.max(lead, 1 - smooth(0.05, 0.08, Math.abs(pv - 0.5)));
      }
      const c = colour(id1);
      // Hand-rolled glass: slow density waves, streaks, darker toward the came, the odd seed bubble.
      let mott = 0.78 + 0.3 * vnoise(px / (ppc * 2.2), py / (ppc * 2.2)) + 0.1 * vnoise(px / (ppc * 0.5), py / (ppc * 4));
      mott *= 0.72 + 0.28 * smooth(lw, lw + 0.3, d);
      if (id1 < 8) {
        if (pattern === 'kiriko') { const f2 = Math.abs(((px + py) / (ppc * 2.2)) % 1 - 0.5) + Math.abs(((px - py + 9999) / (ppc * 2.2)) % 1 - 0.5); mott *= f2 > 0.66 ? 1.25 : 0.88; }
        if (pattern === 'shibori') { const qx = (px / (ppc * 2.6)) % 1 - 0.5, qy = (py / (ppc * 2.6)) % 1 - 0.5; if (Math.abs(Math.hypot(qx, qy) - 0.28) < 0.05) mott *= 1.4; }
        if (pattern === 'kinran' && hash(Math.floor(px / 3), Math.floor(py / 3)) > 0.985) mott *= 1.8;
        if (hash(px, py) > 0.997) mott *= 1.35;
      }
      for (let ch = 0; ch < 3; ch++) img.data[k + ch] = Math.max(0, Math.min(255, c[ch] * mott * (1 - lead) + 4 * lead));
      img.data[k + 3] = Math.round(alpha * 255);
    }
  }
  return img;
}

function star(ctx, x, y, r, fill) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  for (let k = 0; k < 8; k++) {
    const a = k * Math.PI / 4, rr = k % 2 ? r * 0.22 : r;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath(); ctx.fill();
}

// The moon of the night as a paper disc (0 new .. 4 full).
function moonDisc(ctx, x, y, r, phase) {
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = '#16141c'; ctx.fill();
  ctx.clip();
  const k = Math.cos((phase / 8) * Math.PI * 2);
  ctx.fillStyle = '#efe3c2';
  ctx.beginPath();
  for (let a = -Math.PI / 2; a <= Math.PI / 2 + 0.001; a += Math.PI / 32) {
    const yy = Math.sin(a), edge = Math.cos(a);
    ctx.lineTo(x + (phase <= 4 ? edge : -edge) * r, y + yy * r);
  }
  for (let a = Math.PI / 2; a >= -Math.PI / 2 - 0.001; a -= Math.PI / 32) {
    const yy = Math.sin(a), edge = Math.cos(a);
    ctx.lineTo(x + (phase <= 4 ? k * edge : -k * edge) * r, y + yy * r);
  }
  ctx.closePath(); ctx.fill();
  ctx.restore();
}

// A whole night: the panes set side by side in a shoji frame of black came,
// that night's moon in the top rail, the lantern names sent to sea cut into
// the bottom rail, and the date and a line about the game below.
// `cols` sets how many panes sit in a row (2 makes a squarer window for phones).
export function renderNight(night, { ppc = 8, label = '', caption = '', cols = 0 } = {}) {
  const phase = night.phase ?? (night.date ? phaseIndex(night.date) : 4);
  const sizes = night.glass.map(g => ({ W: g.w * ppc, H: g.h * ppc }));
  const gap = ppc * 2.2;
  const n = sizes.length, perRow = Math.max(1, Math.min(n, cols || n)), rows = Math.max(1, Math.ceil(n / perRow));
  const cellW = Math.max(1, ...sizes.map(z => z.W)), cellH = Math.max(1, ...sizes.map(z => z.H));
  const rowW = perRow > 1 && cols ? cellW * perRow + gap * (perRow - 1) : sizes.reduce((s, z) => s + z.W, 0) + gap * Math.max(0, n - 1);
  const rowH = cellH * rows + gap * (rows - 1);
  const side = ppc * 3, railTop = ppc * 5, railBot = night.sent?.length ? ppc * 4.5 : ppc * 3;
  const text = (label ? ppc * 4.5 : 0) + (caption ? ppc * 3.5 : 0);
  const W = Math.round(rowW + side * 2), H = Math.round(rowH + railTop + railBot + text);
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  // Black paper with a few long fibres.
  ctx.fillStyle = '#050408'; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,240,215,0.03)'; ctx.lineWidth = 1;
  for (let k = 0; k < 90; k++) {
    const x = hash(k, 1) * W, y = hash(k, 2) * H, a = hash(k, 3) * Math.PI;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * ppc * 4, y + Math.sin(a) * ppc * 4); ctx.stroke();
  }
  // The light box behind the panes, and the frame's hairlines.
  const fx = side * 0.5, fy = railTop * 0.55, fw = W - side, fh = rowH + railTop * 0.45 + railBot * 0.6;
  const wash = ctx.createLinearGradient(0, fy, 0, fy + fh);
  wash.addColorStop(0, '#11142a'); wash.addColorStop(0.5, '#181c34'); wash.addColorStop(1, '#0c0e1c');
  ctx.fillStyle = wash; ctx.fillRect(fx, fy, fw, fh);
  ctx.lineWidth = Math.max(2, ppc * 0.9); ctx.strokeStyle = '#000'; ctx.strokeRect(fx, fy, fw, fh);
  ctx.lineWidth = Math.max(1, ppc * 0.14); ctx.strokeStyle = 'rgba(239,227,194,0.22)';
  ctx.strokeRect(fx + ppc * 0.7, fy + ppc * 0.7, fw - ppc * 1.4, fh - ppc * 1.4);
  moonDisc(ctx, W / 2, fy, ppc * 1.6, phase);
  const top = railTop;
  ctx.fillStyle = '#000';
  if (cols && perRow < n) {
    // Grid: panes centred in equal bays, black mullions and transoms between them.
    night.glass.forEach((g, i) => {
      const cx = i % perRow, cy = Math.floor(i / perRow);
      const bx = side + cx * (cellW + gap), by = top + cy * (cellH + gap);
      drawPane(ctx, g, bx + (cellW - sizes[i].W) / 2, by + (cellH - sizes[i].H) / 2, ppc, { pattern: night.pattern, phase, game: night.game });
    });
    ctx.fillStyle = '#000';
    for (let c = 1; c < perRow; c++) ctx.fillRect(side + c * (cellW + gap) - gap * 0.58, fy, gap * 0.16, fh);
    for (let r = 1; r < rows; r++) ctx.fillRect(fx, top + r * (cellH + gap) - gap * 0.58, fw, gap * 0.16);
  } else {
    let x = side;
    night.glass.forEach((g, i) => {
      drawPane(ctx, g, x, top + (rowH - sizes[i].H) / 2, ppc, { pattern: night.pattern, phase, game: night.game });
      x += sizes[i].W;
      // A black mullion between panes.
      if (i < night.glass.length - 1) { ctx.fillStyle = '#000'; ctx.fillRect(x + gap * 0.42, fy, gap * 0.16, fh); }
      x += gap;
    });
  }
  ctx.textAlign = 'center';
  if (night.sent?.length) {
    ctx.fillStyle = 'rgba(239,227,194,0.55)';
    ctx.font = `${Math.round(ppc * 1.5)}px ${uiFont()}`;
    ctx.fillText(night.sent.join('・'), W / 2, fy + fh - ppc * 1.4, fw - ppc * 4);
  }
  if (label) {
    ctx.fillStyle = '#e6d9bb';
    ctx.font = `${Math.round(ppc * 2.7)}px ${uiFont()}`;
    ctx.fillText(label, W / 2, H - (caption ? ppc * 5 : ppc * 2));
  }
  if (caption) {
    ctx.fillStyle = '#a49a88';
    ctx.font = `${Math.round(ppc * 2.1)}px ${uiFont()}`;
    ctx.fillText(caption, W / 2, H - ppc * 1.8);
  }
  return cv;
}

// Shop preview: three overlapping pieces of the night's glass with `pattern` cut in.
export function patternSwatch(pattern, { phase = 4, ppc = 10 } = {}) {
  const w = 30, h = 18, n = w * h;
  const layers = Array.from({ length: 4 }, () => new Float32Array(n));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = x + y * w;
    const k = Math.hypot(x - 8, y - 8) < 8 ? 0 : Math.hypot(x - 22, y - 7) < 8 ? 1 : y > 9 ? 2 : 3;
    layers[k][i] = 1;
  }
  const tmp = new Float32Array(n);
  for (const f of layers) blurInPlace(w, h, f, tmp);
  const gels = nightGelHex(phase, CRAB_HEX).map(rgbOf);
  const cv = document.createElement('canvas'); cv.width = w * ppc; cv.height = h * ppc;
  const ctx = cv.getContext('2d');
  const colour = id => [gels[1], gels[2], gels[4], gels[3]][id];
  ctx.putImageData(shadeGlass(ctx, { layers, w, h, ppc, colour, pattern }), 0, 0);
  ctx.lineWidth = ppc * 1.2; ctx.strokeStyle = '#040306'; ctx.strokeRect(0, 0, cv.width, cv.height);
  return cv;
}

// An empty shoji frame, waiting for the first night's glass.
export function emptyWindow(phase = 4, ppc = 6) {
  const W = ppc * 60, H = ppc * 30;
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#050408'; ctx.fillRect(0, 0, W, H);
  const fx = ppc * 2, fy = ppc * 3, fw = W - ppc * 4, fh = H - ppc * 5;
  const wash = ctx.createLinearGradient(0, fy, 0, fy + fh);
  wash.addColorStop(0, '#11142a'); wash.addColorStop(0.5, '#181c34'); wash.addColorStop(1, '#0c0e1c');
  ctx.fillStyle = wash; ctx.fillRect(fx, fy, fw, fh);
  ctx.lineWidth = ppc * 0.9; ctx.strokeStyle = '#000'; ctx.strokeRect(fx, fy, fw, fh);
  ctx.lineWidth = Math.max(1, ppc * 0.14); ctx.strokeStyle = 'rgba(239,227,194,0.22)';
  ctx.strokeRect(fx + ppc * 0.7, fy + ppc * 0.7, fw - ppc * 1.4, fh - ppc * 1.4);
  // Empty mullions: one bay for each pool of a night.
  ctx.fillStyle = '#000';
  for (let k = 1; k < 4; k++) ctx.fillRect(fx + (fw * k) / 4 - ppc * 0.25, fy, ppc * 0.5, fh);
  moonDisc(ctx, W / 2, fy, ppc * 1.8, phase);
  return cv;
}
